import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { resolveId } from "@medplum/core";
import { useMedplum } from "@medplum/react";

import { useDisclosure } from "@mantine/hooks";

import * as Sentry from "@sentry/react";

import { usePracticeId } from "@/hooks/usePracticeId";
import { showSystemErrorNotification } from "@/utils/notifications";

import {
  fetchAllEncountersForExport,
  fetchEncountersForExport,
} from "../services/export-encounter";
import {
  fetchAllFilesForExport,
  fetchAllFilesForExportAsData,
  FILES_EXPORT_PAGE_SIZE,
} from "../services/export-files";
import { fetchAllLabOrdersForExport, fetchLabOrdersForExport } from "../services/export-labs";
import { EncounterData, FileData, LabOrderData } from "../types/export";

export function useClientExportData(clientId: string, opened: boolean, practiceIdProp?: string) {
  const medplum = useMedplum();
  const [isLoading, { open: startLoading, close: stopLoading }] = useDisclosure(false);
  const [pages, setPages] = useState<Record<string, number>>({
    encounters: 1,
    labOrders: 1,
    files: 1,
  });
  const practiceIdFromHooks = usePracticeId();
  const [practiceId, setPracticeId] = useState<string>(practiceIdProp ?? practiceIdFromHooks ?? "");
  const [encountersData, setEncountersData] = useState<EncounterData[]>([]);
  const [labOrdersData, setLabOrdersData] = useState<LabOrderData[]>([]);
  const [allFilesData, setAllFilesData] = useState<FileData[]>([]);
  const [labOrdersTotal, setLabOrdersTotal] = useState(0);
  const [encountersTotal, setEncountersTotal] = useState(0);
  const hasFetchedFilesRef = useRef(false);

  useEffect(() => {
    if (practiceIdProp) {
      setPracticeId(practiceIdProp);
    } else if (practiceIdFromHooks) {
      setPracticeId(practiceIdFromHooks);
    }
  }, [practiceIdProp, practiceIdFromHooks]);

  useEffect(() => {
    if (opened) return;
    setPages({
      encounters: 1,
      labOrders: 1,
      files: 1,
    });
    setAllFilesData([]);
    hasFetchedFilesRef.current = false;
  }, [opened]);

  useEffect(() => {
    if (!opened) return;

    const fetchData = async () => {
      try {
        startLoading();

        let currentPracticeId = practiceId;
        if (!currentPracticeId) {
          const patient = await medplum.readResource("Patient", clientId);
          currentPracticeId = resolveId(patient.managingOrganization) ?? "";
          setPracticeId(currentPracticeId);
        }

        const encounters = await fetchEncountersForExport(
          medplum,
          clientId,
          pages.encounters,
          currentPracticeId,
        );
        setEncountersData(encounters.data);
        setEncountersTotal(encounters.total);

        // Fetch lab orders with Needs Review or Reviewed status
        const labOrders = await fetchLabOrdersForExport(
          medplum,
          clientId,
          pages.labOrders,
          currentPracticeId,
        );
        setLabOrdersData(labOrders.data);
        setLabOrdersTotal(labOrders.total);

        if (!hasFetchedFilesRef.current) {
          hasFetchedFilesRef.current = true;
          const files = await fetchAllFilesForExportAsData(medplum, clientId, currentPracticeId);
          setAllFilesData(files);
        }
      } catch (error) {
        Sentry.captureException(error);
        showSystemErrorNotification();
      } finally {
        stopLoading();
      }
    };

    fetchData();
  }, [opened, clientId, medplum, startLoading, stopLoading, pages, practiceId]);

  const changePage = useCallback((page: number, type: "encounters" | "labOrders" | "files") => {
    setPages((prev) => ({ ...prev, [type]: page }));
  }, []);

  const selectAllEncountersCallback = useMemo(
    () =>
      encountersTotal > 1
        ? async () => {
            try {
              startLoading();
              return await fetchAllEncountersForExport(medplum, clientId, practiceId);
            } finally {
              stopLoading();
            }
          }
        : undefined,
    [encountersTotal, medplum, clientId, startLoading, stopLoading, practiceId],
  );

  const selectAllLabOrdersCallback = useMemo(
    () =>
      labOrdersTotal > 1
        ? async () => {
            try {
              startLoading();
              return await fetchAllLabOrdersForExport(medplum, clientId, practiceId);
            } finally {
              stopLoading();
            }
          }
        : undefined,
    [labOrdersTotal, medplum, clientId, startLoading, stopLoading, practiceId],
  );

  const filesTotal = Math.ceil(allFilesData.length / FILES_EXPORT_PAGE_SIZE);
  const filesData = useMemo(() => {
    const start = (pages.files - 1) * FILES_EXPORT_PAGE_SIZE;
    return allFilesData.slice(start, start + FILES_EXPORT_PAGE_SIZE);
  }, [allFilesData, pages.files]);

  const selectAllFilesCallback = useMemo(
    () =>
      filesTotal > 1
        ? async () => {
            try {
              startLoading();
              return await fetchAllFilesForExport(medplum, clientId, practiceId);
            } finally {
              stopLoading();
            }
          }
        : undefined,
    [filesTotal, medplum, clientId, startLoading, stopLoading, practiceId],
  );

  return {
    isLoading,
    practiceId,
    encountersData,
    labOrdersData,
    filesData,
    encountersTotal,
    labOrdersTotal,
    filesTotal,
    changePage,
    currentPage: pages,
    fetchAllEncounterIds: selectAllEncountersCallback,
    fetchAllLabOrderIds: selectAllLabOrdersCallback,
    fetchAllFileIds: selectAllFilesCallback,
  };
}
